import {
  backupEnvelopeV1Schema,
  backupPayloadV1Schema,
  BACKUP_V1_MAX_CIPHERTEXT_BASE64_LENGTH,
  BACKUP_V1_MAX_ITERATIONS,
  BACKUP_V1_MIN_ITERATIONS,
  type BackupEnvelopeV1,
  type BackupPayloadV1,
} from './types'

export interface CryptoPolicy {
  iterations: number
}

export const DEFAULT_CRYPTO_POLICY: Readonly<CryptoPolicy> = Object.freeze({
  iterations: BACKUP_V1_MAX_ITERATIONS,
})

export const BACKUP_ERROR_MESSAGE = '备份密码错误或文件已损坏'

const PURPOSE = new TextEncoder().encode('carebaby-backup-v1')
const SALT_BYTES = 16
const IV_BYTES = 12
const BASE64_CHUNK_BYTES = 32_768
const CANONICAL_BASE64_PATTERN =
  /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u

function encodeBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK_BYTES) {
    const end = Math.min(offset + BASE64_CHUNK_BYTES, bytes.length)
    let chunk = ''
    for (let index = offset; index < end; index += 1) {
      chunk += String.fromCharCode(bytes[index] ?? 0)
    }
    binary += chunk
  }
  return btoa(binary)
}

function decodeBase64(
  value: string,
  maxEncodedLength: number,
): Uint8Array<ArrayBuffer> {
  if (
    value.length === 0 ||
    value.length > maxEncodedLength ||
    value.length % 4 !== 0 ||
    !CANONICAL_BASE64_PATTERN.test(value)
  ) {
    throw backupError()
  }
  const binary = atob(value)
  if (btoa(binary) !== value) throw backupError()
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

async function deriveKey(
  password: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<CryptoKey> {
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    passwordKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

function backupError(): Error {
  return new Error(BACKUP_ERROR_MESSAGE)
}

export async function encryptBackup(
  payload: BackupPayloadV1,
  password: string,
  policy: CryptoPolicy = DEFAULT_CRYPTO_POLICY,
): Promise<BackupEnvelopeV1> {
  const validatedPayload = backupPayloadV1Schema.parse(payload)
  if (
    Array.from(password).length < 8 ||
    !Number.isInteger(policy.iterations) ||
    policy.iterations < BACKUP_V1_MIN_ITERATIONS ||
    policy.iterations > BACKUP_V1_MAX_ITERATIONS
  ) {
    throw backupError()
  }
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES))
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
  const key = await deriveKey(password, salt, policy.iterations)
  const plaintext = new TextEncoder().encode(JSON.stringify(validatedPayload))
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: PURPOSE },
    key,
    plaintext,
  )
  return {
    formatVersion: 1,
    kdf: {
      name: 'PBKDF2',
      hash: 'SHA-256',
      iterations: policy.iterations,
      saltBase64: encodeBase64(salt),
    },
    cipher: { name: 'AES-GCM', ivBase64: encodeBase64(iv) },
    ciphertextBase64: encodeBase64(new Uint8Array(ciphertext)),
  }
}

export async function decryptBackup(
  envelope: unknown,
  password: string,
): Promise<BackupPayloadV1> {
  try {
    const validatedEnvelope = backupEnvelopeV1Schema.parse(envelope)
    const salt = decodeBase64(validatedEnvelope.kdf.saltBase64, 24)
    const iv = decodeBase64(validatedEnvelope.cipher.ivBase64, 16)
    const ciphertext = decodeBase64(
      validatedEnvelope.ciphertextBase64,
      BACKUP_V1_MAX_CIPHERTEXT_BASE64_LENGTH,
    )
    if (
      salt.byteLength !== SALT_BYTES ||
      iv.byteLength !== IV_BYTES ||
      ciphertext.byteLength < 16
    ) {
      throw backupError()
    }
    const key = await deriveKey(password, salt, validatedEnvelope.kdf.iterations)
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv, additionalData: PURPOSE },
      key,
      ciphertext,
    )
    const parsed: unknown = JSON.parse(new TextDecoder().decode(plaintext))
    return backupPayloadV1Schema.parse(parsed)
  } catch {
    throw backupError()
  }
}
